import React from "react";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import {
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import SelectableCopyList from "../components/SelectableCopyList";
import { useSourceFiles } from "../hooks/useSourceEntities";
import { useAlreadyCopied, useCopyFiles } from "../hooks/useCopyLog";
import type { CopyOperationResult } from "../types";

interface Props {
  destPortalId: string;
  sourcePortalId: string;
  onResult: (r: CopyOperationResult) => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith("image/")) return <ImageIcon sx={{ fontSize: 22, color: "#4CAF50" }} />;
  if (mimeType.startsWith("video/")) return <VideoIcon sx={{ fontSize: 22, color: "#8B5CF6" }} />;
  if (mimeType.startsWith("audio/")) return <AudioIcon sx={{ fontSize: 22, color: "#F59E0B" }} />;
  if (mimeType === "application/pdf") return <PdfIcon sx={{ fontSize: 22, color: "#EF4444" }} />;
  return <FileIcon sx={{ fontSize: 22, color: "#6B7280" }} />;
}

const FilesImportPanel: React.FC<Props> = ({
  destPortalId,
  sourcePortalId,
  onResult,
}) => {
  const { data: files = [], isLoading: loadingList } =
    useSourceFiles(sourcePortalId || null);
  const { data: copied, isLoading: loadingCopied } = useAlreadyCopied(
    destPortalId,
    sourcePortalId || null,
    "file",
  );
  const copyMutation = useCopyFiles();

  if (!sourcePortalId) {
    return <Alert severity="info">Select a source portal above to see its files.</Alert>;
  }

  const rows = files.map((f) => ({
    id: f.id,
    primary: (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {getFileIcon(f.mime_type)}
        <span>{f.file_name}</span>
      </Box>
    ),
    secondary: formatBytes(f.size_bytes),
    meta: (
      <Chip
        label={f.mime_type.split("/")[1] ?? f.mime_type}
        size="small"
        variant="outlined"
      />
    ),
  }));

  const handleCopy = async (ids: string[]) => {
    const items = ids.map((id) => {
      const file = files.find((f) => f.id === id)!;
      return { id: file.id, name: file.file_name };
    });
    const result = await copyMutation.mutateAsync({
      destPortalId,
      sourcePortalId,
      items,
    });
    onResult(result);
  };

  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        Files are copied between portal S3 buckets. Only files with a{" "}
        <strong>clean</strong> scan status are shown.
      </Alert>
      <Typography variant="caption" color="text.secondary">
        Showing {files.length} file{files.length === 1 ? "" : "s"} from the source portal.
      </Typography>
      <SelectableCopyList
        rows={rows}
        alreadyCopiedIds={copied?.source_entity_ids ?? []}
        loading={loadingList || loadingCopied}
        emptyMessage="No files in the source portal."
        onCopy={handleCopy}
        isCopying={copyMutation.isPending}
        ctaLabel="Copy selected files"
      />
    </Stack>
  );
};

export default FilesImportPanel;
